/**
 * One Property: its releases, its price list, and everything inside it.
 *
 * **The screen draws a tree, and draws only the levels that exist.**
 *
 * ```
 * Property        Greenwich Heights          always
 * └── Phase       Phase 1A                   optional
 *     └── Block   Tower A                    optional
 *         └── Unit  Apt 402 (floor 4)        the thing sold
 * ```
 *
 * A land scheme with no phases and no blocks is drawn as the flat list it has always been — no
 * phase headings, no indentation, no "0 blocks" anywhere. That is not a nicety, it is the whole
 * promise of ADR-0103: the tree was added for the developer who sells towers, and the developer
 * who sells bare plots was promised they would never see it. Every `if` below that guards a phase
 * heading or an indent is that promise being kept, and `verify-developer-ui.mjs` asserts it by
 * building a plain scheme beside a nested one and comparing what is drawn.
 *
 * **Every noun comes from the scheme.** The container is a Property; what is inside it is a plot,
 * a home or a unit according to that Property's own `sells` setting, and `noun` on the response is
 * what decides. Nothing here writes either word by hand.
 *
 * **Two refusals are shown rather than paraphrased.** A sellable thing may not contain a sellable
 * thing, and a live sale may not be orphaned in silence. Both come back from the API with a message
 * that names the fix — "Tower A is for sale. Mark it unsellable before putting its units on sale."
 * — and this screen prints that sentence as it arrived. Rewording it here would be a second copy
 * of a rule, drifting from the first.
 */

import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { formValues, useApiMutation } from "@/lib/mutations";
import {
  COMMISSION_BASIS_INFO,
  PHASE_STATUSES,
  PHASE_STATUS_LABELS,
  PLOT_KIND_INFO,
  isPhaseStatus,
  isPlotKind,
  kindsFor,
  soldNoun,
} from "@rentorng/core";
import { minor, pluralise, titleCase, useFormat } from "@/lib/format";
import {
  COMMISSION_SOURCE_LABEL,
  commissionBpFrom,
  commissionLabel,
  commissionPercentValue,
} from "@/lib/development";
import { useSession } from "@/lib/session";
import { Chips, type ChipSpec } from "@/components/chips";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardTitle,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LinkButton,
  Modal,
  PageHeader,
  Select,
  Spinner,
  Switch,
  Table,
  Textarea,
} from "@/components/ui";
import { MoneyInput } from "@/components/money-input";
import { PhotoGallery, PhotoThumbnail } from "@/components/gallery";
import { Refusal } from "@/components/padlock";
import { ReservationDefaultsCard, ReserveModal } from "./Reservations";

/* --------------------------------------------------------------- the shapes */

/** A release of the scheme. There are none on most schemes, and that is the ordinary case. */
interface Phase {
  id: string;
  name: string;
  status: string;
  releaseDate: string | null;
  completionDate: string | null;
  description: string | null;
  position: number;
  commissionBp: number | null;
}

/** One line of the price list: what a thing of this description is being asked for. */
interface PriceType {
  id: string;
  phaseId: string | null;
  name: string;
  price: number | null;
  priceLabel: string | null;
  sizeSqm: number | null;
  kind: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  description: string | null;
  position: number;
  commissionBp: number | null;
}

/**
 * One node of the tree — a block, a unit inside one, or a standalone thing.
 *
 * `level` is derived by the server from the row's relationships rather than stored, so this screen
 * and the API cannot disagree about what a block is. `askingPrice` and `commissionBpEffective` are
 * resolved there too, for the same reason: two screens walking the same chain is two screens that
 * can walk it differently.
 */
interface Node {
  id: string;
  parentId: string | null;
  phaseId: string | null;
  typeId: string | null;
  sellable: boolean;
  label: string;
  floor: string | null;
  status: string;
  sizeSqm: number | null;
  price: number | null;
  commissionBp: number | null;
  kind: string | null;
  units: number;
  listed: boolean;
  buyerName: string | null;
  purchaseId: string | null;
  level: "block" | "unit" | "standalone";
  kindLabel: string;
  typeName: string | null;
  phaseName: string | null;
  askingPrice: number | null;
  askingPriceLabel: string | null;
  askingPriceFrom: "type" | "node" | null;
  commissionBpEffective: number | null;
  commissionFrom: "node" | "type" | "phase" | "estate" | null;
}

interface TreeResponse {
  estate: { id: string; name: string; sells: string; commissionBp: number | null };
  noun: { one: string; many: string };
  phases: Phase[];
  types: PriceType[];
  nodes: Node[];
  tree: Array<Node & { children: Node[] }>;
  totals: { nodes: number; blocks: number; sellable: number; available: number };
}

/** The overview beside the tree: the scheme's own record, its gallery, and the money. */
interface Overview {
  estate: {
    id: string;
    name: string;
    status: string;
    city: string | null;
    state: string | null;
    addressLine: string | null;
    titleType: string | null;
    description: string | null;
    sells: string;
    commissionBp: number | null;
    /* When the commission is earned. On the scheme only — the rate inherits four levels because a
       developer genuinely prices a corner plot differently, but *when* somebody is paid is a policy
       of the business rather than a fact about one plot. ADR-0115. */
    commissionBasis: string | null;
    /* How this property reserves. Defaults, copied onto each reservation and frozen there. */
    reservationFeeBasis: string;
    reservationFeeValue: number | null;
    reservationHolds: boolean;
    reservationRefundable: boolean;
    reservationDays: number | null;
  };
  noun: { one: string; many: string };
  photos: Array<{ id: string; name: string; position: number }>;
  /* The layout plan, on its own and never inside `photos`. Null on most properties, which is the
     ordinary case and the reason the card below has a real empty state rather than a spinner. */
  layout: LayoutPlan | null;
  plots: Array<{ id: string; leadPhotoId: string | null }>;
  totals: {
    plots: number;
    homes: number;
    blocks: number;
    sellable: number;
    available: number;
    listed: number;
    purchases: number;
    soldValue: number;
    soldValueLabel: string;
  };
}

/** A sale on this scheme, read so a re-price can be shown not to have touched one. */
interface Sale {
  id: string;
  reference: string;
  status: string;
  buyerName: string;
  plotLabel: string | null;
  priceLabel: string;
}

const PLOT_TONE: Record<string, "success" | "warn" | "brand" | "muted" | "danger"> = {
  available: "success",
  reserved: "warn",
  allocated: "brand",
  built: "brand",
  handed_over: "muted",
  withdrawn: "danger",
};

const PHASE_TONE: Record<string, "success" | "warn" | "brand" | "muted"> = {
  planning: "muted",
  selling: "success",
  sold_out: "brand",
  closed: "warn",
};

/** A cell that says nothing, written once so an empty column looks the same everywhere. */
const Nothing = () => <span className="text-ink-400">—</span>;

/**
 * The tree's own table, rather than the shared one.
 *
 * `Table` draws every row twice — once as a table for a wide screen and once as a card for a
 * narrow one — which is right for a flat list and cannot be made to work for a hierarchy. A card
 * has no left margin to put a flat inside its building, so the grouping the whole screen exists to
 * show is the first thing lost; and because a node's row is a component with its own state, the
 * card copy has nothing to introspect and renders the row's cells loose in a `div`, which is both
 * invalid markup and a second copy of every row in the document.
 *
 * A tree is one of the few lists where scrolling sideways is the honest answer, so this one is a
 * table at every width. Everything else on this screen — the phases, the price list — is a flat
 * list and keeps the shared control and its cards.
 */
function TreeTable({ head, children }: { head: ReactNode; children: ReactNode }) {
  return (
    <div className="overflow-x-auto overflow-y-hidden">
      <table className="table">
        <thead>{head}</thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------- phases */

/**
 * Phases, as a lens across one list rather than a level to navigate into.
 *
 * **This is the change that answers the complaint.** The operator could not tell what was what, and
 * the mechanism behind that was the drill-down: opening *into* Phase 2 is what loses sight of the
 * scheme. Item 3 of `14-DEVELOPMENT-MODULE-PLAN.md` settles the replacement — chips across the top
 * of the one list, each carrying its own count and price range, **so the chips are also the
 * summary**. The reader narrows without going anywhere.
 *
 * Four things follow from that, and each was decided rather than assumed:
 *
 * - **A status dot, not an icon.** There is no natural picture of "Phase 2", so an icon would be
 *   decoration to be learned. A dot says selling · not released · sold out, which is what a
 *   developer wants at a glance.
 * - **Created and edited inline.** A phase is a name and two dates. Sending somebody to a separate
 *   screen for that is how this module got heavy in the first place.
 * - **Reordered by dragging, and that order is the public site's order too**, or the app and the
 *   marketplace disagree about which release comes first.
 * - **Renaming is free and safe**, because a document already issued keeps the words it was issued
 *   with — the same freezing that protects an agreed price. A renamed phase does not rewrite
 *   somebody's offer letter.
 */

/** What the phase editor is holding while somebody types. */
interface PhaseDraft {
  name: string;
  status: string;
  releaseDate: string;
  completionDate: string;
}

const BLANK_PHASE: PhaseDraft = { name: "", status: "planning", releaseDate: "", completionDate: "" };

/**
 * The inline editor: a name, a state and two dates, and nothing else.
 *
 * Where it is creating the **first** phase it also asks what goes into it, because the operator's
 * rule is that where there are phases, everything belongs to one. Without that question the act of
 * creating a phase would silently invent a bucket of unfiled stock, which is the thing the rule
 * exists to prevent.
 */
function PhaseEditor({
  estateId,
  phase,
  first,
  stock,
  noun,
  onDone,
}: {
  estateId: string;
  /** The phase being edited, or null to create one. */
  phase: Phase | null;
  /** Whether this would be the scheme's first phase. */
  first: boolean;
  /** What is already in the property, for the question the first phase has to ask. */
  stock: Node[];
  noun: { one: string; many: string };
  onDone: () => void;
}) {
  const [draft, setDraft] = useState<PhaseDraft>(
    phase
      ? {
          name: phase.name,
          status: phase.status,
          releaseDate: phase.releaseDate ?? "",
          completionDate: phase.completionDate ?? "",
        }
      : BLANK_PHASE,
  );
  const [fileEverything, setFileEverything] = useState(true);

  const invalidates = [["estate", estateId, "tree"], ["estate", estateId, "phases"], ["estate", estateId], ["estates"]];

  const save = useApiMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      if (phase) return api.patch(`/phases/${phase.id}`, input);
      const created = await api.post<{ id: string }>("/phases", { ...input, estateId });
      /*
        Everything already in the property moves into the first phase, one request per item.

        There is no endpoint that files a set of them at once, and inventing a loop here is the
        honest version of what the operator asked for — *"where there are phases, everything belongs
        to one"*. It runs only on the first phase and only over what is already there, so it is a
        handful of requests on the one occasion it happens rather than a pattern.
      */
      if (first && fileEverything) {
        for (const node of stock) {
          if (node.phaseId) continue;
          await api.patch(`/plots/${node.id}`, { phaseId: created.id });
        }
      }
      return created;
    },
    invalidates,
    onSuccess: onDone,
  });

  const remove = useApiMutation({
    mutationFn: () => api.delete(`/phases/${phase!.id}`),
    invalidates,
    onSuccess: onDone,
  });

  return (
    <Card className="mb-4">
      <CardTitle hint="A phase is a release of the property. It has a name, a state and its dates.">
        {phase ? `Edit ${phase.name}` : "Add a phase"}
      </CardTitle>

      {save.errorMessage ? (
        <div className="mb-3">
          <Refusal error={save.error} message={save.errorMessage} />
        </div>
      ) : null}
      {remove.errorMessage ? (
        <div className="mb-3">
          <Alert tone="danger">{remove.errorMessage}</Alert>
        </div>
      ) : null}

      <form
        className="grid gap-4 sm:grid-cols-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate({
            name: draft.name,
            status: draft.status,
            releaseDate: draft.releaseDate || null,
            completionDate: draft.completionDate || null,
          });
        }}
      >
        <div className="sm:col-span-2">
          <Field label="Name" htmlFor="phase-name" required>
            <Input
              id="phase-name"
              required
              placeholder="Phase 1"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </Field>
        </div>
        <Field label="State" htmlFor="phase-status">
          <Select
            id="phase-status"
            value={draft.status}
            onChange={(event) => setDraft({ ...draft, status: event.target.value })}
          >
            {PHASE_STATUSES.map((code) => (
              <option key={code} value={code}>
                {PHASE_STATUS_LABELS[code]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Released" htmlFor="phase-release" hint="When it goes on sale.">
          <Input
            id="phase-release"
            type="date"
            value={draft.releaseDate}
            onChange={(event) => setDraft({ ...draft, releaseDate: event.target.value })}
          />
        </Field>
        <Field label="Completes" htmlFor="phase-completion" hint="When it is expected to be finished.">
          <Input
            id="phase-completion"
            type="date"
            value={draft.completionDate}
            onChange={(event) => setDraft({ ...draft, completionDate: event.target.value })}
          />
        </Field>

        {/*
          The question the first phase has to ask.

          Once a property has a phase, everything in it belongs to one — the operator's decision, and
          it is what avoids a "Not in a phase" chip nobody wants to look at. Creating the first one
          therefore has to say what happens to what is already there, rather than leaving it behind
          in a bucket the reader discovers later.
        */}
        {first && !phase && stock.length ? (
          <div className="sm:col-span-4">
            <Checkbox
              checked={fileEverything}
              onChange={(event) => setFileEverything(event.target.checked)}
              label={`Put the ${stock.length} ${stock.length === 1 ? noun.one : noun.many} already in this property into this phase`}
              hint="Where a property uses phases, everything in it belongs to one. Anything left out would sit outside every phase."
            />
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2 sm:col-span-4">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? "Saving…" : phase ? "Save the phase" : "Add the phase"}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
          {phase ? (
            <span className="ml-auto">
              <Button
                type="button"
                variant="ghost"
                disabled={remove.isPending}
                onClick={() => {
                  if (window.confirm(`Delete ${phase.name}?`)) remove.mutate(undefined as never);
                }}
              >
                Delete
              </Button>
            </span>
          ) : null}
        </div>
      </form>
    </Card>
  );
}

/* --------------------------------------------------------------- price list */

/**
 * The price list. **One edit re-prices two hundred plots, and touches no sale.**
 *
 * Price used to sit on each individual thing, which made a price rise across a scheme two hundred
 * edits — so nobody did it, and the figures on the screen stopped being the figures being quoted.
 * A type is "these are all the same thing at the same price": every sellable node points at one,
 * and its own price overrides where that one is genuinely different.
 *
 * **The reassurance is the point of the card, not a footnote.** A developer will not use a control
 * that re-prices in bulk until they are certain it cannot reach a sale they have already agreed —
 * so the sales agreed on each type are listed underneath it, at the figures they were agreed at,
 * and they are still listed there after the re-price with the same figures. `purchases.price` is
 * written once when the sale is created and by nothing afterwards (ADR-0104); this is that fact,
 * shown rather than promised.
 */
function PriceListCard({
  estateId,
  types,
  phases,
  nodes,
  sales,
  sells,
  noun,
  canEdit,
}: {
  estateId: string;
  types: PriceType[];
  phases: Phase[];
  nodes: Node[];
  sales: Sale[];
  sells: string;
  noun: { one: string; many: string };
  canEdit: boolean;
}) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  /* What the price was before the last save, so the card can say what moved and what did not. */
  const [repriced, setRepriced] = useState<{ name: string; was: string; sales: number } | null>(null);

  const invalidates = [
    ["estate", estateId, "tree"],
    ["estate", estateId],
    ["purchases"],
  ];

  const kinds = kindsFor(sells);

  /**
   * The sales agreed against each type, matched by label.
   *
   * A label is unique within a scheme — the API enforces it on every create, every edit and every
   * split — so a purchase's `plotLabel` identifies the node it is against without a second request
   * for ids. That is what makes "these three sales are unaffected" a list of real names.
   */
  const soldByType = useMemo(() => {
    const labelToType = new Map<string, string>();
    for (const node of nodes) if (node.typeId) labelToType.set(node.label, node.typeId);
    const out = new Map<string, Sale[]>();
    for (const sale of sales) {
      if (sale.status === "cancelled" || !sale.plotLabel) continue;
      const typeId = labelToType.get(sale.plotLabel);
      if (!typeId) continue;
      out.set(typeId, [...(out.get(typeId) ?? []), sale]);
    }
    return out;
  }, [nodes, sales]);

  const create = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post("/plot-types", input),
    invalidates,
    onSuccess: () => setAdding(false),
  });

  const update = useApiMutation({
    mutationFn: ({ id, ...input }: { id: string } & Record<string, unknown>) => api.patch(`/plot-types/${id}`, input),
    invalidates,
    onSuccess: () => setEditing(null),
  });

  const remove = useApiMutation({
    mutationFn: (id: string) => api.delete(`/plot-types/${id}`),
    invalidates,
  });

  const failure = create.errorMessage ?? update.errorMessage ?? remove.errorMessage;

  const form = (type: PriceType | null) => {
    const key = type?.id ?? "new";
    const agreed = type ? (soldByType.get(type.id) ?? []) : [];
    return (
      <form
        className="grid gap-4 border-b border-ink-200 bg-ink-50 p-4 sm:grid-cols-4"
        onSubmit={(event) => {
          event.preventDefault();
          const values = formValues(event.currentTarget);
          const input = {
            phaseId: values.phaseId || null,
            name: values.name,
            price: values.price ? minor(values.price) : null,
            sizeSqm: values.sizeSqm ? Number(values.sizeSqm) : null,
            kind: values.kind || null,
            bedrooms: values.bedrooms ? Number(values.bedrooms) : null,
            bathrooms: values.bathrooms ? Number(values.bathrooms) : null,
            description: values.description || null,
            commissionBp: commissionBpFrom(values.commissionBp),
          };
          if (type) {
            setRepriced({ name: type.name, was: type.priceLabel ?? "not priced", sales: agreed.length });
            update.mutate({ id: type.id, ...input });
          } else {
            setRepriced(null);
            create.mutate({ estateId, ...input });
          }
        }}
      >
        <div className="sm:col-span-2">
          <Field
            label="Name"
            htmlFor={`type-name-${key}`}
            required
            hint="What a buyer would ask for by name. For example, 500sqm corner, or 3-bed terrace."
          >
            <Input id={`type-name-${key}`} name="name" required defaultValue={type?.name ?? ""} placeholder="500sqm corner" />
          </Field>
        </div>
        <Field label="Asking price" htmlFor={`type-price-${key}`} hint="What is asked for one of these. The agreed price is recorded on the sale itself.">
          <MoneyInput id={`type-price-${key}`} name="price" defaultValue={type?.price ?? ""} placeholder="18,000,000" />
        </Field>
        <Field label="Commission rate" htmlFor={`type-commission-${key}`} hint="Per cent. Leave it empty to use the phase's or the property's rate.">
          <Input
            id={`type-commission-${key}`}
            name="commissionBp"
            type="number"
            min={0}
            max={100}
            step="0.01"
            defaultValue={commissionPercentValue(type?.commissionBp)}
            placeholder="2.5"
          />
        </Field>
        {/* Offered only where the scheme has phases. A price list narrowed to a release nobody has
            created is a control with one option and no purpose. */}
        {phases.length ? (
          <Field label="Phase" htmlFor={`type-phase-${key}`} hint="Narrow it to one release, or leave it across the whole property.">
            <Select id={`type-phase-${key}`} name="phaseId" defaultValue={type?.phaseId ?? ""}>
              <option value="">Across the whole property</option>
              {phases.map((phase) => (
                <option key={phase.id} value={phase.id}>
                  {phase.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <Field label="Size (m²)" htmlFor={`type-size-${key}`}>
          <Input id={`type-size-${key}`} name="sizeSqm" type="number" min={0} defaultValue={type?.sizeSqm ?? ""} placeholder="500" />
        </Field>
        <Field label="Type" htmlFor={`type-kind-${key}`}>
          <Select id={`type-kind-${key}`} name="kind" defaultValue={type?.kind ?? ""}>
            <option value="">Not specified</option>
            {kinds.map((info) => (
              <option key={info.value} value={info.value}>
                {info.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Bedrooms" htmlFor={`type-bedrooms-${key}`}>
          <Input id={`type-bedrooms-${key}`} name="bedrooms" type="number" min={0} max={100} placeholder="3" defaultValue={type?.bedrooms ?? ""} />
        </Field>
        <Field label="Bathrooms" htmlFor={`type-bathrooms-${key}`}>
          <Input id={`type-bathrooms-${key}`} name="bathrooms" type="number" min={0} max={100} placeholder="2" defaultValue={type?.bathrooms ?? ""} />
        </Field>
        <div className="sm:col-span-4">
          <Field label="Specification" htmlFor={`type-description-${key}`}>
            <Input
              id={`type-description-${key}`}
              name="description"
              defaultValue={type?.description ?? ""}
              placeholder="Corner position, fully serviced, road and drainage."
            />
          </Field>
        </div>

        {/*
          Said before the button is pressed, not after.

          The whole reason a developer hesitates over a bulk re-price is the fear that it reaches
          somebody who has already signed. It cannot: the agreed figure is copied onto the purchase
          when the sale is made and nothing writes it afterwards. The sales it cannot reach are
          named underneath, so this is a claim somebody can check rather than one they must take.
        */}
        {type ? (
          <div className="sm:col-span-4">
            <Alert tone="info" title="Re-pricing this changes what is asked of the next buyer, and nothing else.">
              {agreed.length === 0
                ? `Nothing on this type has been sold yet, so there is nothing a new price could disturb.`
                : `${pluralise(agreed.length, "sale has", "sales have")} already been agreed on this type. ${
                    agreed.length === 1 ? "It keeps" : "They keep"
                  } the figure ${agreed.length === 1 ? "it was" : "they were"} agreed at.`}
            </Alert>
            {agreed.length ? (
              <ul className="mt-2 grid gap-1 text-xs text-ink-600 sm:grid-cols-2">
                {agreed.map((sale) => (
                  <li key={sale.id} data-agreed-sale={sale.reference}>
                    <Link className="font-medium text-brand-700 hover:underline" to={`/app/development/sales/${sale.id}`}>
                      {sale.buyerName}
                    </Link>{" "}
                    · {sale.plotLabel} · agreed at {sale.priceLabel}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        <div className="flex gap-2 sm:col-span-4">
          <Button type="submit" disabled={create.isPending || update.isPending}>
            {type ? "Save the price" : "Add the type"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setAdding(false);
              setEditing(null);
            }}
          >
            Cancel
          </Button>
        </div>
      </form>
    );
  };

  return (
    <Card padded={false} className="mb-6">
      <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 p-4">
        <CardTitle hint={`What each kind of ${noun.one} is being asked for. Re-pricing a type re-prices every ${noun.one} on it in one edit.`}>
          Price list
        </CardTitle>
        {canEdit ? (
          <div className="ml-auto">
            <Button
              variant="secondary"
              onClick={() => {
                setEditing(null);
                setAdding((open) => !open);
              }}
            >
              {adding ? "Close" : "Add a price"}
            </Button>
          </div>
        ) : null}
      </div>

      {failure ? (
        <div className="border-b border-ink-200 p-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      ) : null}

      {/* The proof, after the fact. It names the old figure and the sales that did not follow it. */}
      {repriced && update.isSuccess ? (
        <div className="border-b border-ink-200 p-4">
          <Alert tone="success" title={`${repriced.name} was re-priced.`}>
            {repriced.sales === 0
              ? `It was ${repriced.was}. Nothing had been sold on this type, so no sale was touched.`
              : `It was ${repriced.was}. ${pluralise(repriced.sales, "agreed sale is", "agreed sales are")} unchanged — ${
                  repriced.sales === 1 ? "it stands" : "they stand"
                } at the figure ${repriced.sales === 1 ? "it was" : "they were"} agreed at, shown against the type below.`}{" "}
            <button type="button" className="font-semibold underline" onClick={() => setRepriced(null)}>
              Dismiss
            </button>
          </Alert>
        </div>
      ) : null}

      {adding ? form(null) : null}

      {types.length === 0 ? (
        <EmptyState
          title="No price list."
          hint={`Each ${noun.one} carries its own price until there is one. A type is worth adding at the point where several ${noun.many} are the same thing at the same figure.`}
        />
      ) : (
        <Table
          head={
            <tr>
              <th>Type</th>
              {phases.length ? <th>Phase</th> : null}
              <th className="num">Asking price</th>
              <th className="num">Size</th>
              <th className="num">On this type</th>
              <th className="num">Available</th>
              <th>Commission</th>
              {canEdit ? <th /> : null}
            </tr>
          }
        >
          {types.map((type) => {
            const on = nodes.filter((node) => node.typeId === type.id);
            const available = on.filter((node) => node.sellable && node.status === "available").length;
            const agreed = soldByType.get(type.id) ?? [];
            return (
              <Fragment key={type.id}>
                <tr>
                  <td className="font-medium">
                    {type.name}
                    <div className="text-xs font-normal text-ink-500">
                      {[
                        type.kind && isPlotKind(type.kind) ? PLOT_KIND_INFO[type.kind].label : null,
                        type.bedrooms ? `${type.bedrooms} bed` : null,
                        type.bathrooms ? `${type.bathrooms} bath` : null,
                        type.description,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  </td>
                  {phases.length ? (
                    <td className="text-ink-600">
                      {phases.find((phase) => phase.id === type.phaseId)?.name ?? (
                        <span className="text-xs text-ink-400">Whole property</span>
                      )}
                    </td>
                  ) : null}
                  <td className="num font-medium">
                    {type.priceLabel ?? <span className="text-xs font-normal text-ink-400">On application</span>}
                  </td>
                  <td className="num text-ink-600">{type.sizeSqm ? `${type.sizeSqm} m²` : <Nothing />}</td>
                  <td className="num text-ink-600">{on.length}</td>
                  <td className="num text-ink-600">{available}</td>
                  <td className="text-ink-600">
                    {commissionLabel(type.commissionBp) ?? <span className="text-xs text-ink-400">Inherited</span>}
                  </td>
                  {canEdit ? (
                    <td>
                      <span className="flex flex-wrap items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAdding(false);
                            setEditing((open) => (open === type.id ? null : type.id));
                          }}
                        >
                          {editing === type.id ? "Close" : "Re-price"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Delete ${type.name} from the price list?`)) remove.mutate(type.id);
                          }}
                        >
                          Delete
                        </Button>
                      </span>
                    </td>
                  ) : null}
                </tr>
                {/* Named on the row itself as well as inside the form, because the reassurance has
                    to be visible to somebody who has not yet decided to open the form. */}
                {agreed.length ? (
                  <tr>
                    <td colSpan={9} className="bg-ink-50 text-xs text-ink-600">
                      {pluralise(agreed.length, "sale", "sales")} agreed at this price —{" "}
                      {agreed.map((sale) => `${sale.buyerName} at ${sale.priceLabel}`).join(", ")}. A new asking price
                      does not reach {agreed.length === 1 ? "it" : "them"}.
                    </td>
                  </tr>
                ) : null}
                {editing === type.id ? (
                  <tr>
                    <td colSpan={9} className="p-0">
                      {form(type)}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </Table>
      )}
    </Card>
  );
}

/* ------------------------------------------------------- whole ↔ split */

/** A live sale that has to be given somewhere to land before a switch can go through. */
interface Affected {
  purchaseId: string;
  buyerName: string;
  nodeLabel: string;
}

/** What the developer said happens to one of them. `to` is a label for a split, an id for a merge. */
interface MoveChoice {
  to: string;
  reason: string;
}

/**
 * **Splitting a building into the things that are actually sold.**
 *
 * Allowed even after a sale — the first design refused it, the user overruled that, and was right:
 * a developer who sold a townhouse, had the buyer default and now wants six flats out of it has an
 * ordinary need. What is refused is doing it in *silence*, so the dialog carries one extra question
 * per live buyer, and it is the buyer's name that is asked about rather than a row id.
 *
 * It is one request rather than three steps because there is no order in which the three steps are
 * individually legal: creating the flats first is refused (a sellable thing may not contain a
 * sellable thing), and taking the building off sale first orphans the buyer, because the flat they
 * are to be moved to does not exist yet. So a move here may name a child **by label** — one of the
 * ones this same dialog is about to create — and the server resolves the id after the rows land.
 * ADR-0104.
 */
function SplitModal({
  node,
  estateId,
  affected,
  types,
  noun,
  onClose,
}: {
  node: Node;
  estateId: string;
  affected: Affected[];
  types: PriceType[];
  noun: { one: string; many: string };
  onClose: () => void;
}) {
  /* Two rows to begin with, because a building being split is being split into at least two. */
  const [children, setChildren] = useState([
    { label: "", floor: "", sizeSqm: "", price: "", typeId: "" },
    { label: "", floor: "", sizeSqm: "", price: "", typeId: "" },
  ]);
  const [moves, setMoves] = useState<Record<string, MoveChoice>>(
    Object.fromEntries(affected.map((sale) => [sale.purchaseId, { to: "", reason: "" }])),
  );
  /* The quick fill for a tower: "Apt" 401 to 412 is twelve rows nobody should type. */
  const [prefix, setPrefix] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const split = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/plots/${node.id}/split`, input),
    invalidates: [["estate", estateId, "tree"], ["estate", estateId], ["estates"], ["purchases"], ["developer-summary"]],
    onSuccess: onClose,
  });

  const labels = children.map((child) => child.label.trim()).filter(Boolean);

  const generate = () => {
    const first = Number(from);
    const last = Number(to);
    if (!prefix.trim() || !Number.isFinite(first) || !Number.isFinite(last) || last < first) return;
    /* Capped at the server's own limit rather than a different one, so the dialog never builds a
       request the API is going to refuse. */
    const made = [];
    for (let n = first; n <= Math.min(last, first + 499); n += 1) {
      made.push({ label: `${prefix.trim()} ${n}`, floor: "", sizeSqm: "", price: "", typeId: "" });
    }
    setChildren(made);
  };

  const submit = () => {
    split.mutate({
      children: children
        .filter((child) => child.label.trim())
        .map((child) => ({
          label: child.label.trim(),
          floor: child.floor.trim() || null,
          sizeSqm: child.sizeSqm ? Number(child.sizeSqm) : null,
          price: child.price ? minor(child.price) : null,
          typeId: child.typeId || null,
        })),
      moves: affected.map((sale) => {
        const choice = moves[sale.purchaseId] ?? { to: "", reason: "" };
        return choice.to === "cancel"
          ? { purchaseId: sale.purchaseId, cancel: true, reason: choice.reason || null }
          : { purchaseId: sale.purchaseId, toLabel: choice.to || null, reason: choice.reason || null };
      }),
    });
  };

  /* Nothing may be submitted until every buyer has somewhere to land. The server refuses it anyway
     — by name — but a button that leads straight to a refusal is a button that should have been
     disabled, and the reason has to be on the screen rather than in the answer. */
  const unanswered = affected.filter((sale) => !(moves[sale.purchaseId]?.to ?? ""));

  return (
    <Modal open onClose={onClose} title={`Split ${node.label}`}>
      <div className="space-y-4">
        <p className="text-sm text-ink-600">
          {node.label} stops being for sale and becomes a block. What is inside it is what sells from then on. Its
          photographs, its label and its history are kept.
        </p>

        {split.errorMessage ? <Alert tone="danger">{split.errorMessage}</Alert> : null}

        {/* The quick fill, above the rows it fills. A tower is "Apt 401" to "Apt 412". */}
        <div className="grid gap-2 rounded-[var(--radius-control)] border border-ink-200 p-3 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <Field label="Naming prefix" htmlFor="split-prefix" hint="Fills the rows below. Each is still editable afterwards.">
              <Input id="split-prefix" value={prefix} onChange={(event) => setPrefix(event.target.value)} placeholder="Apt" />
            </Field>
          </div>
          <Field label="From" htmlFor="split-from">
            <Input id="split-from" type="number" value={from} onChange={(event) => setFrom(event.target.value)} placeholder="401" />
          </Field>
          <Field label="To" htmlFor="split-to">
            <Input id="split-to" type="number" value={to} onChange={(event) => setTo(event.target.value)} placeholder="412" />
          </Field>
          <div className="sm:col-span-4">
            <Button type="button" variant="secondary" size="sm" onClick={generate}>
              Fill the rows
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            What {node.label} becomes
          </p>
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {children.map((child, index) => (
              <div key={index} className="grid gap-2 sm:grid-cols-5">
                <div className="sm:col-span-2">
                  <Input
                    aria-label={`Label of ${noun.one} ${index + 1}`}
                    name={`child-label-${index}`}
                    value={child.label}
                    placeholder={`${titleCase(noun.one)} ${index + 1}`}
                    onChange={(event) =>
                      setChildren((rows) => rows.map((row, i) => (i === index ? { ...row, label: event.target.value } : row)))
                    }
                  />
                </div>
                <Input
                  aria-label={`Floor of ${noun.one} ${index + 1}`}
                  value={child.floor}
                  placeholder="Floor"
                  onChange={(event) =>
                    setChildren((rows) => rows.map((row, i) => (i === index ? { ...row, floor: event.target.value } : row)))
                  }
                />
                <MoneyInput
                  aria-label={`Price of ${noun.one} ${index + 1}`}
                  value={child.price}
                  placeholder="Price"
                  onChange={(next) =>
                    setChildren((rows) => rows.map((row, i) => (i === index ? { ...row, price: next } : row)))
                  }
                />
                <Select
                  aria-label={`Price list line for ${noun.one} ${index + 1}`}
                  value={child.typeId}
                  onChange={(event) =>
                    setChildren((rows) => rows.map((row, i) => (i === index ? { ...row, typeId: event.target.value } : row)))
                  }
                >
                  <option value="">Not on the price list</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setChildren((rows) => [...rows, { label: "", floor: "", sizeSqm: "", price: "", typeId: "" }])}
          >
            Add another
          </Button>
        </div>

        {/*
          The extra question, and only when it is needed.

          Nothing sold underneath and this whole section is absent: the dialog is a plain
          confirmation. Something sold, and it carries the one thing "Yes, I'm sure" cannot say —
          which of the new ones that buyer now holds, or that the sale is cancelled outright.
        */}
        {affected.length ? (
          <div className="space-y-3 rounded-[var(--radius-control)] border border-warn-200 bg-warn-50 p-3">
            <p className="text-sm font-medium text-ink-900">
              {pluralise(affected.length, "sale", "sales")} against {node.label}. Say where{" "}
              {affected.length === 1 ? "it" : "each"} lands.
            </p>
            {affected.map((sale) => (
              <div key={sale.purchaseId} className="grid gap-2 sm:grid-cols-2">
                <Field
                  label={`${sale.buyerName} has bought ${sale.nodeLabel}`}
                  htmlFor={`move-${sale.purchaseId}`}
                  required
                >
                  <Select
                    id={`move-${sale.purchaseId}`}
                    value={moves[sale.purchaseId]?.to ?? ""}
                    onChange={(event) =>
                      setMoves((current) => ({
                        ...current,
                        [sale.purchaseId]: { ...(current[sale.purchaseId] ?? { reason: "" }), to: event.target.value },
                      }))
                    }
                  >
                    <option value="">Which one does that become?</option>
                    {labels.map((label) => (
                      <option key={label} value={label}>
                        {label}
                      </option>
                    ))}
                    <option value="cancel">Cancel the sale instead</option>
                  </Select>
                </Field>
                <Field
                  label="Reason"
                  htmlFor={`move-reason-${sale.purchaseId}`}
                  hint="Goes on the statement, so it is written for the buyer to read."
                >
                  <Input
                    id={`move-reason-${sale.purchaseId}`}
                    value={moves[sale.purchaseId]?.reason ?? ""}
                    placeholder="The building was re-cut into flats."
                    onChange={(event) =>
                      setMoves((current) => ({
                        ...current,
                        [sale.purchaseId]: { ...(current[sale.purchaseId] ?? { to: "" }), reason: event.target.value },
                      }))
                    }
                  />
                </Field>
              </div>
            ))}
            {unanswered.length ? (
              <p className="text-xs text-ink-600">
                {unanswered.map((sale) => sale.buyerName).join(", ")} still {unanswered.length === 1 ? "has" : "have"}{" "}
                nowhere to land. The price {unanswered.length === 1 ? "they" : "each"} agreed is kept either way.
              </p>
            ) : null}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={split.isPending || labels.length === 0 || unanswered.length > 0} onClick={submit}>
            {split.isPending ? "Splitting…" : `Split into ${labels.length || 0} ${labels.length === 1 ? noun.one : noun.many}`}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The reverse: sell the block whole again.
 *
 * The same rule pointing the other way, and the same one extra question. A buyer of Apt 402 cannot
 * be left holding a flat that is no longer for sale, so they either become the building or the sale
 * is cancelled — and the building is the only place they can land, which is why the choice is short.
 *
 * **The flats are kept, not deleted.** Twelve rows' labels, photographs and history should not go
 * on one click, and a developer who changes their mind twice in a fortnight would otherwise lose
 * all of it. They come off sale and stay where they are.
 */
function WholeModal({
  node,
  estateId,
  children,
  affected,
  noun,
  onClose,
}: {
  node: Node;
  estateId: string;
  children: Node[];
  affected: Affected[];
  noun: { one: string; many: string };
  onClose: () => void;
}) {
  const [moves, setMoves] = useState<Record<string, MoveChoice>>(
    Object.fromEntries(affected.map((sale) => [sale.purchaseId, { to: "", reason: "" }])),
  );

  const whole = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post(`/plots/${node.id}/whole`, input),
    invalidates: [["estate", estateId, "tree"], ["estate", estateId], ["estates"], ["purchases"], ["developer-summary"]],
    onSuccess: onClose,
  });

  const unanswered = affected.filter((sale) => !(moves[sale.purchaseId]?.to ?? ""));

  const submit = () => {
    whole.mutate({
      moves: affected.map((sale) => {
        const choice = moves[sale.purchaseId] ?? { to: "", reason: "" };
        return choice.to === "cancel"
          ? { purchaseId: sale.purchaseId, cancel: true, reason: choice.reason || null }
          : { purchaseId: sale.purchaseId, toPlotId: node.id, reason: choice.reason || null };
      }),
    });
  };

  const sellable = children.filter((child) => child.sellable);

  return (
    <Modal open onClose={onClose} title={`Sell ${node.label} whole`}>
      <div className="space-y-4">
        <p className="text-sm text-ink-600">
          {node.label} becomes the thing being sold, and the {sellable.length === 1 ? noun.one : noun.many} inside it
          come off sale. Nothing is deleted — {sellable.length === 1 ? "it stays" : "they stay"} on the property with
          {sellable.length === 1 ? " its" : " their"} photographs and history, simply not for sale.
        </p>

        {whole.errorMessage ? <Alert tone="danger">{whole.errorMessage}</Alert> : null}

        {sellable.length ? (
          <p className="text-sm text-ink-600">
            <span className="font-medium">Coming off sale:</span> {sellable.map((child) => child.label).join(", ")}.
          </p>
        ) : null}

        {affected.length ? (
          <div className="space-y-3 rounded-[var(--radius-control)] border border-warn-200 bg-warn-50 p-3">
            <p className="text-sm font-medium text-ink-900">
              {pluralise(affected.length, "sale", "sales")} inside {node.label}. Say where{" "}
              {affected.length === 1 ? "it" : "each"} lands.
            </p>
            {affected.map((sale) => (
              <div key={sale.purchaseId} className="grid gap-2 sm:grid-cols-2">
                <Field label={`${sale.buyerName} has bought ${sale.nodeLabel}`} htmlFor={`merge-${sale.purchaseId}`} required>
                  <Select
                    id={`merge-${sale.purchaseId}`}
                    value={moves[sale.purchaseId]?.to ?? ""}
                    onChange={(event) =>
                      setMoves((current) => ({
                        ...current,
                        [sale.purchaseId]: { ...(current[sale.purchaseId] ?? { reason: "" }), to: event.target.value },
                      }))
                    }
                  >
                    <option value="">What does that become?</option>
                    <option value="whole">{node.label}, sold whole</option>
                    <option value="cancel">Cancel the sale instead</option>
                  </Select>
                </Field>
                <Field label="Reason" htmlFor={`merge-reason-${sale.purchaseId}`} hint="Goes on the statement.">
                  <Input
                    id={`merge-reason-${sale.purchaseId}`}
                    value={moves[sale.purchaseId]?.reason ?? ""}
                    placeholder="The flats were put back together."
                    onChange={(event) =>
                      setMoves((current) => ({
                        ...current,
                        [sale.purchaseId]: { ...(current[sale.purchaseId] ?? { to: "" }), reason: event.target.value },
                      }))
                    }
                  />
                </Field>
              </div>
            ))}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button type="button" disabled={whole.isPending || unanswered.length > 0} onClick={submit}>
            {whole.isPending ? "Merging…" : `Sell ${node.label} whole`}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- the tree */

/**
 * One row of the tree, and the panel that opens under it.
 *
 * Indentation is by `level` rather than by a counter, because the level is the server's answer and
 * a counter here would be a second one. There are only two of them — a block and what is in it —
 * so the whole of the tree's geometry is one class name.
 */
function NodeRow({
  node,
  estateId,
  phases,
  types,
  sells,
  noun,
  leadPhotoId,
  canEdit,
  showCommission,
  openPanel,
  setOpenPanel,
  onSplit,
  onWhole,
  canReserve,
  onReserve,
  heading,
  childCount,
}: {
  node: Node;
  estateId: string;
  phases: Phase[];
  types: PriceType[];
  sells: string;
  noun: { one: string; many: string };
  leadPhotoId: string | null;
  canEdit: boolean;
  showCommission: boolean;
  openPanel: string | null;
  setOpenPanel: (next: string | null) => void;
  onSplit: () => void;
  onWhole: () => void;
  /**
   * Reserving this one, where the reader may.
   *
   * A different permission from editing the property — `lease.edit`, the same one taking a sale
   * needs, because a reservation is the stage before a sale rather than a change to the stock. It
   * therefore rides in the actions cell without deciding whether that cell is drawn: somebody who
   * may reserve but not edit reserves from the button on the property's own header instead.
   */
  canReserve: boolean;
  onReserve: () => void;
  /**
   * Draw this row as a **subheading** rather than as a row of cells.
   *
   * A block is "Tower A", with its flats beneath it — a divider inside the list and never somewhere
   * to navigate to. Item 6 of `14-DEVELOPMENT-MODULE-PLAN.md` settles that, and the reason is the
   * same as the reason phases became chips: **every level that becomes a place to go is a level the
   * reader can get lost in**, which is what produced *"I can't tell what is what"*.
   *
   * It keeps its actions cell, so a block can still be edited, sold whole or deleted from where it
   * is drawn. Drawing it as a heading takes away the columns that never applied to it anyway — a
   * block has no price, no status and no buyer, and four cells reading "Not for sale" were four
   * cells of noise on every scheme that used one.
   */
  heading?: boolean;
  /** How many things are inside it, for the subheading to say. */
  childCount?: number;
}) {
  const invalidates = [["estate", estateId, "tree"], ["estate", estateId], ["estates"], ["developer-summary"], ["purchases"]];

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/plots/${node.id}`, input),
    invalidates,
    onSuccess: () => setOpenPanel(null),
  });

  /* Its own mutation, so the switch's refusal is reported beside the switch rather than inside a
     form the developer may not have opened. This is the door the hard refusal comes through. */
  const setFlag = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/plots/${node.id}`, input),
    invalidates,
  });

  const remove = useApiMutation({
    mutationFn: () => api.delete(`/plots/${node.id}`),
    invalidates,
  });

  const kinds = kindsFor(sells);
  const editKey = `${node.id}:edit`;
  const photoKey = `${node.id}:photos`;
  const columns = 7 + (showCommission ? 1 : 0) + (canEdit ? 1 : 0);

  /* Written once and drawn on whichever shape the row takes. A subheading that lost its controls
     would make a block the one thing on the screen that cannot be edited where it is seen. */
  const actionsCell = canEdit ? (
    <td>
      <span className="flex flex-wrap items-center gap-1">
        <Button variant="ghost" size="sm" onClick={() => setOpenPanel(openPanel === photoKey ? null : photoKey)}>
          {openPanel === photoKey ? "Close" : "Photographs"}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpenPanel(openPanel === editKey ? null : editKey)}>
          {openPanel === editKey ? "Close" : "Edit"}
        </Button>
        {/* Only a root may be split or merged: the tree goes two deep, so a unit has nothing to
            become and nothing to be put back into. */}
        {!node.parentId && node.sellable ? (
          <Button variant="ghost" size="sm" onClick={onSplit}>
            Split into units
          </Button>
        ) : null}
        {node.level === "block" ? (
          <Button variant="ghost" size="sm" onClick={onWhole}>
            Sell whole
          </Button>
        ) : null}
        {/* Only what is actually for sale and actually on the market. The API refuses a grouping in
            the same words the split dialog uses, and something already reserved or sold is not this
            control's to offer twice. */}
        {canReserve && node.sellable && node.status === "available" ? (
          <Button variant="ghost" size="sm" onClick={onReserve}>
            Reserve
          </Button>
        ) : null}
      </span>
    </td>
  ) : null;

  /*
    The two panels a row can open, written once and drawn under whichever shape the row took.

    A block is drawn as a subheading and a unit as a row of cells, and both still open the same
    photograph gallery and the same edit form — so the panels are lifted out rather than repeated.
    Two copies of an edit form is two forms that drift, and only one of them gets the next fix.
  */
  const photoPanel = (
        /* Opened under the row rather than on a screen of its own: adding pictures to twelve flats
           should not be twelve navigations and twelve journeys back. */
        <tr>
          <td colSpan={columns} className="bg-ink-50">
            <p className="mb-3 text-sm font-medium">
              Photographs of {node.label}
              <span className="ml-2 font-normal text-ink-500">
                The first is the leading image, and is what the public card uses.
              </span>
            </p>
            <PhotoGallery owner="plot" ownerId={node.id} canEdit={canEdit} noun={noun.one} />
          </td>
        </tr>
  );

  const editPanel = (
        <tr>
          <td colSpan={columns} className="bg-ink-50 p-0">
            <form
              className="grid gap-4 p-4 sm:grid-cols-4"
              onSubmit={(event) => {
                event.preventDefault();
                const values = formValues(event.currentTarget);
                save.mutate({
                  label: values.label,
                  floor: values.floor || null,
                  status: values.status,
                  phaseId: values.phaseId || null,
                  typeId: values.typeId || null,
                  sizeSqm: values.sizeSqm ? Number(values.sizeSqm) : null,
                  price: values.price ? minor(values.price) : null,
                  kind: values.kind || null,
                  commissionBp: commissionBpFrom(values.commissionBp),
                });
              }}
            >
              {save.errorMessage ? (
                <div className="sm:col-span-4">
                  <Alert tone="danger">{save.errorMessage}</Alert>
                </div>
              ) : null}

              <div className="sm:col-span-2">
                <Field label="Label" htmlFor={`node-label-${node.id}`} required>
                  <Input
                    id={`node-label-${node.id}`}
                    name="label"
                    required
                    /* Follows what the Property sells: "Plot 14" on land, "Unit 14" in a tower. An
                       example that contradicts the scheme it is on is worse than none. */
                    placeholder={`${titleCase(noun.one)} 14`}
                    defaultValue={node.label}
                  />
                </Field>
              </div>
              <Field label="Floor" htmlFor={`node-floor-${node.id}`} hint="A label — 4, Ground, Penthouse. Not a level in the tree.">
                <Input id={`node-floor-${node.id}`} name="floor" defaultValue={node.floor ?? ""} placeholder="4" />
              </Field>
              <Field label="Status" htmlFor={`node-status-${node.id}`}>
                <Select id={`node-status-${node.id}`} name="status" defaultValue={node.status}>
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="allocated">Allocated</option>
                  <option value="built">Built</option>
                  <option value="handed_over">Handed over</option>
                  <option value="withdrawn">Withdrawn</option>
                </Select>
              </Field>
              {/* Drawn only where the scheme has releases. Nothing is invented for a scheme without. */}
              {phases.length ? (
                <Field label="Phase" htmlFor={`node-phase-${node.id}`}>
                  <Select id={`node-phase-${node.id}`} name="phaseId" defaultValue={node.phaseId ?? ""}>
                    <option value="">Not in a phase</option>
                    {phases.map((phase) => (
                      <option key={phase.id} value={phase.id}>
                        {phase.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              ) : null}
              <Field label="Price list" htmlFor={`node-type-${node.id}`} hint="The price list line this is one of. Its own price below wins where it is different.">
                <Select id={`node-type-${node.id}`} name="typeId" defaultValue={node.typeId ?? ""}>
                  <option value="">Not on the price list</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                      {type.priceLabel ? ` — ${type.priceLabel}` : ""}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Own asking price" htmlFor={`node-price-${node.id}`} hint="Leave it empty to use the price list.">
                <MoneyInput id={`node-price-${node.id}`} name="price" defaultValue={node.price ?? ""} placeholder="18,000,000" />
              </Field>
              <Field label="Size (m²)" htmlFor={`node-size-${node.id}`}>
                <Input id={`node-size-${node.id}`} name="sizeSqm" type="number" min={0} placeholder="500" defaultValue={node.sizeSqm ?? ""} />
              </Field>
              <Field label="Type" htmlFor={`node-kind-${node.id}`}>
                <Select id={`node-kind-${node.id}`} name="kind" defaultValue={node.kind ?? ""}>
                  <option value="">Not specified</option>
                  {kinds.map((info) => (
                    <option key={info.value} value={info.value}>
                      {info.label}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Commission rate"
                htmlFor={`node-commission-${node.id}`}
                hint={
                  node.commissionFrom && node.commissionFrom !== "node"
                    ? `Empty means ${commissionLabel(node.commissionBpEffective)}, ${COMMISSION_SOURCE_LABEL[node.commissionFrom]}.`
                    : "Per cent. Leave it empty to inherit."
                }
              >
                <Input
                  id={`node-commission-${node.id}`}
                  name="commissionBp"
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  defaultValue={commissionPercentValue(node.commissionBp)}
                  placeholder="2.5"
                />
              </Field>

              {/*
                The switch the hard refusal comes through.

                A sellable thing may not contain a sellable thing — Tower A for sale *and* Apt 402
                for sale is the same square metre sold to two people. The API refuses it in both
                directions with a message that names the fix, and that message is printed above
                exactly as it arrived. Splitting and merging are their own actions because they
                have to move a buyer at the same moment; this is the plain flag for everything else.
              */}
              <div className="sm:col-span-4">
                <div className="flex flex-wrap items-center gap-3 rounded-[var(--radius-control)] border border-ink-200 bg-white p-3">
                  <Switch
                    checked={node.sellable}
                    disabled={setFlag.isPending}
                    label={`${node.label} is what is sold`}
                    onChange={(next) => setFlag.mutate({ sellable: next })}
                  />
                  <span className="min-w-0 flex-1 text-sm">
                    <span className="font-medium">{node.sellable ? "This is what is sold" : "A grouping only"}</span>
                    <span className="block text-xs text-ink-500">
                      {node.sellable
                        ? `A buyer buys ${node.label} itself. Nothing inside it may be for sale at the same time.`
                        : `${node.label} groups what is inside it. What is inside is what sells.`}
                    </span>
                  </span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 sm:col-span-4">
                <Button type="submit" disabled={save.isPending}>
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setOpenPanel(null)}>
                  Cancel
                </Button>
                <span className="ml-auto">
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Delete ${node.label}?`)) remove.mutate(undefined as never);
                    }}
                  >
                    Delete
                  </Button>
                </span>
              </div>
            </form>
          </td>
        </tr>
  );

  if (heading) {
    return (
      <Fragment>
        <tr data-node={node.label} data-level="block" data-heading="1" className="bg-ink-50">
          <td colSpan={columns - (canEdit ? 1 : 0)}>
            <span className="flex flex-wrap items-center gap-3">
              <PhotoThumbnail photoId={leadPhotoId} alt="" className="size-9" />
              <span className="text-sm font-semibold text-ink-800">{node.label}</span>
              <span className="text-xs text-ink-500">{node.kindLabel}</span>
              <span className="text-xs text-ink-400">
                {childCount === 0
                  ? `nothing inside it yet`
                  : `${pluralise(childCount ?? 0, noun.one, noun.many)} inside`}
              </span>
            </span>
          </td>
          {actionsCell}
        </tr>

        {/* The refusal, verbatim, beside the control that provoked it. */}
        {setFlag.errorMessage || remove.errorMessage ? (
          <tr>
            <td colSpan={columns}>
              <Alert tone="danger">{setFlag.errorMessage ?? remove.errorMessage}</Alert>
            </td>
          </tr>
        ) : null}

        {openPanel === photoKey ? photoPanel : null}
        {openPanel === editKey ? editPanel : null}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <tr data-node={node.label} data-level={node.level}>
        <td className="font-medium">
          <span className={`flex items-center gap-2 ${node.level === "unit" ? "pl-6" : ""}`}>
            {/* The line that makes a unit read as being inside the row above it rather than beside
                it. Drawn only for a unit, so a scheme with no blocks has no rule down its left. */}
            {node.level === "unit" ? <span aria-hidden className="text-ink-300">└</span> : null}
            <PhotoThumbnail photoId={leadPhotoId} alt="" className="size-10" />
            <span className="min-w-0">
              <span className="block truncate">{node.label}</span>
              <span className="block text-xs font-normal text-ink-500">
                {[node.floor ? `Floor ${node.floor}` : null, node.level === "block" ? "Block" : null]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </span>
          </span>
        </td>
        <td className="text-ink-600">
          {node.kindLabel}
          {node.typeName ? <div className="text-xs text-ink-500">{node.typeName}</div> : null}
        </td>
        <td className="num text-ink-600">{node.sizeSqm ? `${node.sizeSqm} m²` : <Nothing />}</td>
        <td className="num">
          {/* A block is not priced. It is not for sale, so a figure against it would be a figure
              nobody could be asked for. */}
          {node.level === "block" ? (
            <span className="text-xs text-ink-400">Not for sale</span>
          ) : node.askingPriceLabel ? (
            <>
              {node.askingPriceLabel}
              <div className="text-xs font-normal text-ink-400">
                {node.askingPriceFrom === "type" ? "from the price list" : "set here"}
              </div>
            </>
          ) : (
            <span className="text-xs text-ink-400">On application</span>
          )}
        </td>
        <td>
          {node.level === "block" ? (
            <Badge tone="muted">grouping</Badge>
          ) : (
            <Badge tone={PLOT_TONE[node.status] ?? "muted"}>{node.status.replace("_", " ")}</Badge>
          )}
        </td>
        <td>
          {node.buyerName && node.purchaseId ? (
            <Link className="text-brand-700 hover:underline" to={`/app/development/sales/${node.purchaseId}`}>
              {node.buyerName}
            </Link>
          ) : node.level === "block" ? (
            <Nothing />
          ) : (
            <span className="text-ink-400">Unsold</span>
          )}
        </td>
        {showCommission ? (
          <td className="text-ink-600">
            {commissionLabel(node.commissionBpEffective) ?? <Nothing />}
            {node.commissionFrom ? (
              <div className="text-xs text-ink-400">{COMMISSION_SOURCE_LABEL[node.commissionFrom]}</div>
            ) : null}
          </td>
        ) : null}
        <td>
          {/* Offered only where it can do anything. A block is not on the market whatever the
              switch says, and neither is something already allocated — a control that changes
              nothing visible is a control that lies. */}
          {node.sellable && node.status === "available" ? (
            <span className="flex items-center gap-2">
              <Switch
                checked={node.listed}
                disabled={!canEdit || setFlag.isPending}
                label={`Show ${node.label} on the public listing`}
                onChange={(next) => setFlag.mutate({ listed: next })}
              />
              <span className="text-xs text-ink-500">{node.listed ? "Public" : "Private"}</span>
            </span>
          ) : (
            <span className="text-xs text-ink-400">Not for sale</span>
          )}
        </td>
        {actionsCell}
      </tr>

      {/* The refusal, verbatim, beside the control that provoked it. */}
      {setFlag.errorMessage || remove.errorMessage ? (
        <tr>
          <td colSpan={columns}>
            <Alert tone="danger">{setFlag.errorMessage ?? remove.errorMessage}</Alert>
          </td>
        </tr>
      ) : null}

      {openPanel === photoKey ? photoPanel : null}

      {openPanel === editKey ? editPanel : null}
    </Fragment>
  );
}

/* -------------------------------------------------------- the layout plan */

interface LayoutPlan {
  id: string;
  name: string;
  mimeType: string | null;
  sizeBytes: number | null;
  width: number | null;
  height: number | null;
}

/**
 * The drawing a buyer traces their own plot on.
 *
 * **It is not the first photograph, and it is not drawn like one.** A gallery is a row of
 * thumbnails somebody flicks through; a layout plan is one drawing, shown as large as the column
 * allows, that somebody reads — they are looking for plot 42 on it. So it has a card of its own,
 * one file, and a link to open it at full size, which is the only way to read a site plan of two
 * hundred plots on a laptop.
 *
 * **A PDF is drawn as a link rather than embedded.** A surveyor hands over a PDF at least as often
 * as a picture, and the endpoint takes both — but an inline PDF viewer inside a card is a scrollbar
 * inside a scrollbar on the desktop and nothing at all on most phones. One honest link that opens
 * the drawing in the reader the person already has beats a frame that half works.
 *
 * The file is fetched through `/api/v1/development-media/:id`, the agency's own endpoint — never
 * the public one. Most properties are not on the market, and the public route refuses those,
 * correctly; using it here would draw a broken image on nearly every screen.
 */
function LayoutPlanCard({ estateId, plan, canEdit }: { estateId: string; plan: LayoutPlan | null; canEdit: boolean }) {
  const [chosen, setChosen] = useState<File | null>(null);

  /*
    Multipart, by hand, because `api.put` sends JSON.

    The same reason the gallery uploads by hand: base64 in a JSON body makes a five megabyte
    drawing a third bigger on the connection that is already the slow part. The server's own
    sentence is thrown as the error so that "larger than 15MB" and "is not a PDF, JPEG, PNG or
    WebP" reach the person exactly as they were written, rather than being paraphrased here into a
    second copy of a rule.
  */
  const upload = useApiMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("layout", file);
      const response = await fetch(`/api/v1/estates/${estateId}/layout`, {
        method: "PUT",
        body: form,
        credentials: "include",
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error ?? "That layout plan could not be uploaded.");
      }
      return response.json();
    },
    invalidates: [["estate", estateId]],
    onSuccess: () => setChosen(null),
  });

  const remove = useApiMutation({
    mutationFn: () => api.delete(`/estates/${estateId}/layout`),
    invalidates: [["estate", estateId]],
  });

  const isImage = Boolean(plan?.mimeType?.startsWith("image/"));
  const href = plan ? `/api/v1/development-media/${plan.id}` : null;

  return (
    <Card padded={false} className="mb-6">
      <div className="border-b border-ink-200 p-4">
        <CardTitle hint="The site plan or survey drawing — one per property, shown whole. A buyer finds their own plot on it, so it is worth uploading at the size it was drawn.">
          Layout plan
        </CardTitle>
      </div>

      <div className="p-4">
        {upload.errorMessage ? (
          <div className="mb-4">
            <Alert tone="danger">{upload.errorMessage}</Alert>
          </div>
        ) : null}
        {remove.errorMessage ? (
          <div className="mb-4">
            <Alert tone="danger">{remove.errorMessage}</Alert>
          </div>
        ) : null}

        {plan && href ? (
          <div className="space-y-3">
            {isImage ? (
              <a href={href} target="_blank" rel="noreferrer" className="block">
                <img
                  src={href}
                  alt={`Layout plan for this property — ${plan.name}`}
                  className="w-full rounded-[var(--radius-control)] border border-ink-200 bg-white"
                />
              </a>
            ) : (
              <div className="rounded-[var(--radius-control)] border border-dashed border-ink-300 p-6 text-center">
                <p className="text-sm text-ink-700">{plan.name}</p>
                <p className="mt-1 text-xs text-ink-500">A drawing, not a picture. It opens in your reader.</p>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <a
                className="text-sm font-medium text-brand-700 hover:underline"
                href={href}
                target="_blank"
                rel="noreferrer"
              >
                Open it full size
              </a>
              {canEdit ? (
                <Button
                  className="ml-auto"
                  variant="secondary"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(undefined as never)}
                >
                  {remove.isPending ? "Removing…" : "Remove"}
                </Button>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-sm text-ink-600">
            {canEdit
              ? "No layout plan yet. Upload the site plan and it appears here and on the public page."
              : "No layout plan has been uploaded for this property."}
          </p>
        )}

        {canEdit ? (
          <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-ink-200 pt-4">
            <input
              type="file"
              name="layout"
              aria-label={plan ? "Replace the layout plan" : "Upload a layout plan"}
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="text-sm"
              onChange={(event) => setChosen(event.currentTarget.files?.[0] ?? null)}
            />
            <Button
              disabled={!chosen || upload.isPending}
              onClick={() => {
                if (chosen) upload.mutate(chosen);
              }}
            >
              {upload.isPending ? "Uploading…" : plan ? "Replace it" : "Upload it"}
            </Button>
            {/* One drawing, and saying so is cheaper than somebody discovering it by losing one. */}
            <p className="text-xs text-ink-500">
              A PDF or a picture, up to 15MB. Uploading a new one replaces the drawing that is there.
            </p>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

/**
 * The lowest asking price in a set, and the highest.
 *
 * The head of a Property answers *what is left*, and "what is left" is a count and a price. Written
 * over the rows the screen is already holding rather than asked of the server, because the server
 * has already resolved each row's asking price down the chain — node, then price list — and a second
 * implementation of that resolution is exactly what `askingPrice` exists to prevent.
 *
 * Null where nothing in the set carries a price, which is an ordinary state: a scheme that quotes on
 * application has no range, and inventing a zero for it would be worse than saying nothing.
 */
function priceRange(rows: Node[]): { from: number | null; to: number | null } {
  const prices = rows.map((row) => row.askingPrice).filter((price): price is number => price !== null);
  if (!prices.length) return { from: null, to: null };
  return { from: Math.min(...prices), to: Math.max(...prices) };
}

/* ------------------------------------------------------------------- screen */

/**
 * One Property, and **it opens on what is for sale rather than on its structure.**
 *
 * The head answers the only question somebody opens it with — what is left — as counts and a price
 * range. Then the list. The phases are chips across it and the blocks are subheadings inside it, so
 * **neither optional level is a place to go**: one is a lens over the list, the other a divider in
 * it, and neither takes the reader off the page. That is the whole answer to *"I can't tell what is
 * what"*, and nothing about the model changed to get it — only where the reader stands. Item 2 and
 * items 3, 4 and 6 of `14-DEVELOPMENT-MODULE-PLAN.md`.
 *
 * **A scheme with no phases and no blocks meets neither word.** That is not a nicety, it is the
 * promise of ADR-0103 kept: the levels are optional in the schema and were not optional on screen.
 * Every branch below that guards a chip strip or a subheading is that promise, and
 * `verify-developer-ui.mjs` asserts it by building a plain scheme beside a nested one and comparing
 * what is drawn.
 */
export function DevelopmentPropertyDetailPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const fmt = useFormat();
  const canEdit = can("property.edit");
  const [params, setParams] = useSearchParams();
  const [adding, setAdding] = useState(false);
  /* Held in state because it decides whether the unit-count field is asked for at all. */
  const [kind, setKind] = useState("");
  /*
    What is being added: **a plot · a building sold whole · a building split into units**.

    This is where the word "sellable" went. It was a flag on a row that read as jargon, and it is
    really the answer to *"is this the thing you sell, or does it contain the things you sell?"* —
    which is a question with an obvious answer at the moment something is added and none at all
    afterwards. Item 4 of the plan.
  */
  const [shape, setShape] = useState<"units" | "whole" | "split">("units");
  /* Which row has a panel open, and which panel. One at a time: a page of forty open galleries is
     forty requests and nothing anybody can read. */
  const [openPanel, setOpenPanel] = useState<string | null>(null);
  const [splitting, setSplitting] = useState<string | null>(null);
  const [merging, setMerging] = useState<string | null>(null);
  /** Which phase the inline editor is on: a phase to edit, `"new"` to create one, or null. */
  const [editingPhase, setEditingPhase] = useState<string | null>(null);
  /*
    Which reservation dialog is open, if any.

    `undefined` is shut. A string is a particular row; an empty string is the property itself, where
    what is being held has not been chosen yet — an ordinary thing to reserve, and the reason the
    plot is optional on the endpoint as well as here.
  */
  const [reserving, setReserving] = useState<string | undefined>(undefined);

  const overview = useQuery({
    queryKey: ["estate", id],
    queryFn: () => api.get<Overview>(`/estates/${id}`),
  });

  /*
    The hierarchy, in one request.

    The flat list and the nested one both come from the server rather than one being derived from
    the other here — deriving it is how the table and the tree end up ordered differently — and so
    do the resolved asking price and the resolved commission rate, so that no two screens can walk
    the same chain and reach different answers.
  */
  const tree = useQuery({
    queryKey: ["estate", id, "tree"],
    queryFn: () => api.get<TreeResponse>(`/estates/${id}/tree`),
  });

  /*
    Every sale on this scheme, so a re-price can be shown not to have touched one.

    Read once here rather than per price type: a scheme with twelve lines on its price list would
    otherwise be twelve requests for the same list. Allowed to fail quietly — a member who may edit
    properties but not see sales still gets the price list, minus the names.
  */
  const sales = useQuery({
    queryKey: ["purchases", "on-estate", id],
    queryFn: () => api.get<{ purchases: Sale[] }>(`/purchases?estateId=${id}&perPage=200`),
    enabled: can("lease.view"),
    retry: false,
  });

  const invalidates = [
    ["estate", id],
    ["estate", id, "tree"],
    ["estate", id, "phases"],
    ["estates"],
    ["developer-summary"],
    ["sales"],
  ];

  /**
   * Adding stock — **one action, whichever of the three shapes it is.**
   *
   * "Add 50 plots" with a numbering pattern, not fifty forms. A run of individual things is one
   * `POST /plots/bulk`; a building sold whole is one `POST /plots`; a building split into units is a
   * `POST /plots` for the building followed by one bulk run inside it. The third is two requests
   * because there is no endpoint that does both, and doing it as two visits to two forms is the
   * thing this control exists to spare somebody.
   */
  const run = useApiMutation({
    mutationFn: async (input: Record<string, unknown>) => {
      const { form, ...rest } = input as { form: "units" | "whole" | "split" } & Record<string, unknown>;
      if (form === "whole") {
        return api.post<{ id: string }>("/plots", {
          estateId: id,
          label: rest.label,
          sellable: true,
          kind: rest.kind ?? null,
          units: rest.units,
          sizeSqm: rest.sizeSqm ?? null,
          price: rest.price ?? null,
          phaseId: rest.phaseId ?? null,
          typeId: rest.typeId ?? null,
        });
      }
      if (form === "split") {
        /* The building first, marked as a grouping, then its units inside it. If the second call is
           refused the building stands on its own and the run can be finished from the same form —
           which is better than a half-built tower nobody can see. */
        const block = await api.post<{ id: string }>("/plots", {
          estateId: id,
          label: rest.label,
          sellable: false,
          kind: rest.kind ?? null,
          units: rest.units,
          phaseId: rest.phaseId ?? null,
        });
        return api.post<{ created: number; skipped: number }>("/plots/bulk", {
          estateId: id,
          prefix: rest.prefix,
          from: rest.from,
          to: rest.to,
          parentId: block.id,
          phaseId: rest.phaseId ?? null,
          typeId: rest.typeId ?? null,
          sizeSqm: rest.sizeSqm ?? null,
          price: rest.price ?? null,
        });
      }
      return api.post<{ created: number; skipped: number }>("/plots/bulk", { estateId: id, ...rest });
    },
    invalidates,
    onSuccess: () => setAdding(false),
  });

  /**
   * A new order for the phases, written one at a time.
   *
   * `PATCH /phases/:id` takes a position and there is no endpoint that takes a whole order, so the
   * requests go out together and the list is refetched once at the end. **That order is the public
   * site's order too** — the same `position` column the marketplace reads — which is the operator's
   * decision and the reason dragging is worth building at all.
   */
  const reorder = useApiMutation({
    mutationFn: (keys: string[]) =>
      Promise.all(keys.map((key, index) => api.patch(`/phases/${key}`, { position: index }))),
    invalidates,
  });

  /* The rate everything on the scheme inherits, when nothing nearer has an answer. */
  const setRate = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.patch(`/estates/${id}`, input),
    invalidates,
  });

  if (overview.isLoading || tree.isLoading) return <Spinner />;
  if (overview.error || !overview.data) return <ErrorState error={overview.error} retry={() => overview.refetch()} />;
  if (tree.error || !tree.data) return <ErrorState error={tree.error} retry={() => tree.refetch()} />;

  const { estate, totals } = overview.data;
  const { phases, types, nodes } = tree.data;
  const noun = tree.data.noun ?? soldNoun(estate.sells);
  const kinds = kindsFor(estate.sells);
  const holdsUnits = (value: string) => isPlotKind(value) && PLOT_KIND_INFO[value].holdsUnits;

  const leadPhotos = new Map(overview.data.plots.map((plot) => [plot.id, plot.leadPhotoId]));
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const childrenOf = (nodeId: string) => nodes.filter((node) => node.parentId === nodeId);

  /*
    Whether the commission column is drawn at all.

    Nobody has set a rate on a scheme written before the column existed, and a column of dashes is
    a column that makes a screen look unfinished for a developer who wanted none of it. It appears
    the moment a rate is set anywhere on the chain, and disappears again if it is cleared.
  */
  const showCommission = estate.commissionBp !== null || nodes.some((node) => node.commissionBpEffective !== null);

  /** The live sale on one node, in the shape the dialogs ask about. */
  const saleOn = (node: Node): Affected[] =>
    node.purchaseId && node.buyerName
      ? [{ purchaseId: node.purchaseId, buyerName: node.buyerName, nodeLabel: node.label }]
      : [];

  const splittingNode = splitting ? byId.get(splitting) : undefined;
  const mergingNode = merging ? byId.get(merging) : undefined;

  /* ------------------------------------------------------------- the lens */

  /*
    Which phase the reader is looking through.

    In the address, not in state: the Overview's "by phase" table links straight to a release, and a
    filter held in a component cannot be linked to. It falls back to All where the phase named has
    been deleted, rather than showing an empty list with a chip nobody can turn off.
  */
  const askedPhase = params.get("phase");
  const currentPhase = phases.some((phase) => phase.id === askedPhase) ? askedPhase : null;

  const forSale = nodes.filter((node) => node.sellable);
  const inPhase = (phaseId: string | null) =>
    phaseId === null ? nodes : nodes.filter((node) => node.phaseId === phaseId);

  /** Things somebody could buy today, in a set — the figure a price range is a range of. */
  const availableIn = (rows: Node[]) => rows.filter((row) => row.sellable && row.status === "available");

  /** "from ₦18m", or "₦18m – ₦42m". Nothing at all where nothing carries a price. */
  const rangeLabel = (rows: Node[]): string | null => {
    const { from, to } = priceRange(availableIn(rows));
    if (from === null) return null;
    return from === to ? fmt.money(from) : `from ${fmt.money(from)}`;
  };

  /*
    The stock this phase, or the whole property, holds — and what it is worth.

    `unfiled` is drawn as a chip only when it is not empty. Where a property uses phases everything
    in it belongs to one, so this should always be empty; a scheme that has one anyway — an import,
    or a phase deleted from under some rows — must not have those rows become invisible. A chip that
    appears only when it has something in it keeps the rule and loses nothing.
  */
  const unfiled = phases.length ? nodes.filter((node) => !node.phaseId) : [];

  const phaseChips: ChipSpec[] = phases.length
    ? [
        {
          key: "all",
          label: "All",
          to: `?`,
          count: availableIn(nodes).length,
          detail: rangeLabel(nodes) ?? undefined,
        },
        ...phases.map((phase) => ({
          key: phase.id,
          label: phase.name,
          to: `?phase=${phase.id}`,
          count: availableIn(inPhase(phase.id)).length,
          detail: rangeLabel(inPhase(phase.id)) ?? undefined,
          tone: PHASE_TONE[phase.status] ?? "muted",
          toneLabel: isPhaseStatus(phase.status) ? PHASE_STATUS_LABELS[phase.status] : phase.status,
        })),
        ...(unfiled.length
          ? [
              {
                key: "unfiled",
                label: "Outside every phase",
                to: `?phase=none`,
                count: availableIn(unfiled).length,
                tone: "warn" as const,
                toneLabel: "Needs filing",
              },
            ]
          : []),
      ]
    : [];

  /* What the list is showing, once the lens is applied. `?phase=none` is the unfiled chip. */
  const lens =
    params.get("phase") === "none" && unfiled.length ? unfiled : currentPhase ? inPhase(currentPhase) : nodes;

  /*
    The list: roots first, each followed by whatever is inside it.

    There are no phase headings any more — the chips took that job, and a heading for a release the
    reader has just narrowed to would be the same word twice. What remains as a heading is the
    block, which is a divider inside the list rather than a level above it.
  */
  const shownIds = new Set(lens.map((node) => node.id));
  const roots = lens.filter((node) => !node.parentId);
  /* A unit whose block is filtered out still belongs on the screen — otherwise narrowing to a phase
     could hide stock that is in that phase. It is drawn at the top level, under its own label. */
  const orphans = lens.filter((node) => node.parentId && !shownIds.has(node.parentId));

  const columns = 7 + (showCommission ? 1 : 0) + (canEdit ? 1 : 0);

  const editing = editingPhase === "new" ? null : phases.find((phase) => phase.id === editingPhase) ?? null;

  /* One sentence for the head, which is the whole reason somebody opened this page. */
  const headRange = rangeLabel(nodes);

  return (
    <div>
      <PageHeader
        title={estate.name}
        back={{ to: "/app/development/properties", label: "Properties" }}
        subtitle={[estate.city, estate.state].filter(Boolean).join(", ") || undefined}
        description={estate.description ?? `What is for sale in this property, who has bought and what is left.`}
        actions={
          can("lease.edit") ? (
            <>
              {/* Reserving comes before selling, and reads first for that reason. Secondary,
                  because recording the sale is what the developer came here to do. */}
              <Button variant="secondary" onClick={() => setReserving("")}>
                Reserve a {noun.one}
              </Button>
              <LinkButton to={`/app/development/sales/new?estateId=${estate.id}`} need="lease.edit">
                Sell a {noun.one}
              </LinkButton>
            </>
          ) : null
        }
      />

      {/*
        The head answers what is left — **not what the property is made of.**

        Available, reserved, sold and the price they start at, which is the operator's own set. The
        structural figures that used to lead this screen (rows in all, blocks, dwellings) said
        nothing anybody opens a property to find out, and were the first thing a reader had to
        decode.
      */}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-head="what is left">
        <Card>
          <p className="eyebrow">Available</p>
          <p className="tabular mt-1 text-2xl font-semibold">{tree.data.totals.available}</p>
          {/* Available and advertised are two different numbers, and confusing them is how "why is
              my property not on the site" becomes a support call about a switch nobody knew about. */}
          <p className="text-xs text-ink-500">
            {totals.listed === 0 ? "None on the public listing" : `${totals.listed} on the public listing`}
          </p>
        </Card>
        <Card>
          <p className="eyebrow">Reserved</p>
          <p className="tabular mt-1 text-2xl font-semibold">
            {forSale.filter((node) => node.status === "reserved").length}
          </p>
          <p className="text-xs text-ink-500">
            <Link className="text-brand-700 hover:underline" to={`/app/development/sales?stage=reserved&estateId=${id}`}>
              Open the holds
            </Link>
          </p>
        </Card>
        <Card>
          <p className="eyebrow">Sold</p>
          <p className="tabular mt-1 text-2xl font-semibold">{totals.purchases}</p>
          <p className="text-xs text-ink-500">
            <Link className="text-brand-700 hover:underline" to={`/app/development/sales?estateId=${id}`}>
              {totals.soldValueLabel} agreed
            </Link>
          </p>
        </Card>
        <Card>
          <p className="eyebrow">From</p>
          <p className="tabular mt-1 text-2xl font-semibold">{headRange ?? "On application"}</p>
          <p className="text-xs text-ink-500">
            {headRange ? "The lowest asking price still available" : "No asking price is set on anything available"}
          </p>
        </Card>
      </div>

      {estate.titleType ? (
        <div className="mb-6">
          <Alert tone="info" title={`Sold on ${estate.titleType}.`}>
            Recorded on the property so that it is available to every buyer enquiry.
          </Alert>
        </div>
      ) : null}

      {/*
        A refusal that named a plan draws the padlock and the way out of it; everything else is the
        ordinary red box, unchanged. Creating a run of units is exactly the write a ceiling stops, so
        this is the screen where the difference is felt most.
      */}
      {run.errorMessage ? (
        <div className="mb-4">
          <Refusal error={run.error} message={run.errorMessage} />
        </div>
      ) : null}
      {setRate.errorMessage ? (
        <div className="mb-4">
          <Refusal error={setRate.error} message={setRate.errorMessage} />
        </div>
      ) : null}
      {reorder.errorMessage ? (
        <div className="mb-4">
          <Alert tone="danger">{reorder.errorMessage}</Alert>
        </div>
      ) : null}

      {/* ------------------------------------------------------- the lens */}

      {/*
        The chips, drawn only where a phase exists.

        A scheme of fifty bare plots never meets the word, which is the whole point of the levels
        being optional. Somebody who wants one presses the control beside the list below; there is
        no empty chip strip inviting them to learn a concept they have not asked about.
      */}
      {phases.length ? (
        <Chips
          chips={phaseChips}
          current={params.get("phase") === "none" ? "unfiled" : (currentPhase ?? "all")}
          label="Phases"
          onReorder={
            canEdit
              ? (keys) => reorder.mutate(keys.filter((key) => key !== "all" && key !== "unfiled"))
              : undefined
          }
          action={
            canEdit ? (
              <span className="flex items-center gap-1">
                {currentPhase ? (
                  <Button variant="ghost" size="sm" onClick={() => setEditingPhase(currentPhase)}>
                    Edit this phase
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => setEditingPhase("new")}>
                  Add a phase
                </Button>
              </span>
            ) : null
          }
        />
      ) : null}

      {editingPhase ? (
        <PhaseEditor
          estateId={id}
          phase={editing}
          first={phases.length === 0}
          stock={nodes}
          noun={noun}
          onDone={() => setEditingPhase(null)}
        />
      ) : null}

      {/* --------------------------------------------------------- the list */}

      <Card padded={false} className="mb-6">
        <div className="flex flex-wrap items-center gap-2 border-b border-ink-200 p-4">
          <CardTitle
            hint={
              currentPhase
                ? `Everything in this release, and who has it.`
                : `Everything for sale in this property, and who has it.`
            }
          >
            {titleCase(noun.many)}
          </CardTitle>
          {canEdit ? (
            <div className="ml-auto flex flex-wrap items-center gap-1">
              {/* The way in to phases for a property that has none. One control, worded as what it
                  does rather than as the concept it introduces. */}
              {!phases.length && !editingPhase ? (
                <Button variant="ghost" size="sm" onClick={() => setEditingPhase("new")}>
                  Add a phase
                </Button>
              ) : null}
              <Button variant="secondary" onClick={() => setAdding((open) => !open)}>
                {adding ? "Close" : `Add ${noun.many}`}
              </Button>
            </div>
          ) : null}
        </div>

        {adding ? (
          <form
            className="grid gap-4 border-b border-ink-200 p-4 sm:grid-cols-6"
            onSubmit={(event) => {
              event.preventDefault();
              const values = formValues(event.currentTarget);
              run.mutate({
                form: shape,
                label: values.label,
                prefix: values.prefix,
                from: values.from ? Number(values.from) : undefined,
                to: values.to ? Number(values.to) : undefined,
                sizeSqm: values.sizeSqm ? Number(values.sizeSqm) : null,
                price: values.price ? minor(values.price) : null,
                kind: values.kind || null,
                units: values.units ? Number(values.units) : undefined,
                phaseId: values.phaseId || null,
                typeId: values.typeId || null,
                floor: values.floor || null,
              });
            }}
          >
            {/*
              What is being added, in the three shapes a developer actually sells.

              This is the whole of what "sellable" used to be, asked once at the moment it has an
              obvious answer. The words are the operator's: a plot, a building sold whole, or a
              building split into units.
            */}
            <div className="sm:col-span-6">
              <Field label="What is being added" htmlFor="shape">
                <Select id="shape" value={shape} onChange={(event) => setShape(event.target.value as typeof shape)}>
                  <option value="units">
                    {titleCase(noun.many)} — a numbered run, each sold on its own
                  </option>
                  <option value="whole">A building sold whole — one buyer takes all of it</option>
                  <option value="split">A building split into units — the units inside it are sold</option>
                </Select>
              </Field>
            </div>

            {shape === "units" ? (
              <>
                <div className="sm:col-span-2">
                  {/* Re-running an overlapping range skips what exists rather than failing, so a
                      half-finished run can be finished — said here because it is not obvious. */}
                  <Field
                    label="Naming pattern"
                    htmlFor="prefix"
                    required
                    hint={`Each one is named "${"Block C " + titleCase(noun.one)} 1", "${"Block C " + titleCase(noun.one)} 2" and so on. Re-running an overlapping range skips what already exists.`}
                  >
                    <Input id="prefix" name="prefix" required placeholder={`Block C ${titleCase(noun.one)}`} />
                  </Field>
                </div>
                <Field label="From" htmlFor="from" required>
                  <Input id="from" name="from" type="number" min={0} required placeholder="1" />
                </Field>
                <Field label="To" htmlFor="to" required>
                  <Input id="to" name="to" type="number" min={0} required placeholder="50" />
                </Field>
              </>
            ) : (
              <>
                <div className="sm:col-span-3">
                  <Field label="Name of the building" htmlFor="label" required>
                    <Input id="label" name="label" required placeholder="Tower A" />
                  </Field>
                </div>
                {shape === "split" ? (
                  <>
                    <div className="sm:col-span-2">
                      <Field
                        label="Naming pattern for the units"
                        htmlFor="prefix"
                        required
                        hint='Each unit is named "Apt 401", "Apt 402" and so on.'
                      >
                        <Input id="prefix" name="prefix" required placeholder="Apt" />
                      </Field>
                    </div>
                    <Field label="From" htmlFor="from" required>
                      <Input id="from" name="from" type="number" min={0} required placeholder="401" />
                    </Field>
                    <Field label="To" htmlFor="to" required>
                      <Input id="to" name="to" type="number" min={0} required placeholder="412" />
                    </Field>
                  </>
                ) : null}
              </>
            )}

            <Field label="Size (m²)" htmlFor="sizeSqm">
              <Input id="sizeSqm" name="sizeSqm" type="number" min={0} placeholder="500" />
            </Field>
            <div className="sm:col-span-2">
              <Field
                label="Asking price"
                htmlFor="price"
                hint="The listed price for each. The agreed price is recorded on the sale itself."
              >
                <MoneyInput id="price" name="price" placeholder="25,000,000" />
              </Field>
            </div>
            {/* A real list rather than a text box: it is the first thing a buyer asks, and free
                text is the one thing a filter cannot use. Narrowed by what the property sells, so a
                land scheme is never offered warehouses. ADR-0081. */}
            <div className="sm:col-span-2">
              <Field label="Description" htmlFor="kind">
                <Select id="kind" name="kind" value={kind} onChange={(event) => setKind(event.target.value)}>
                  <option value="">Not specified</option>
                  {kinds.map((info) => (
                    <option key={info.value} value={info.value}>
                      {info.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            {/* Asked only where it means something. "How many flats?" about a plot of land is a
                question with one answer and no purpose. */}
            {holdsUnits(kind) || shape !== "units" ? (
              <Field
                label="Dwellings inside"
                htmlFor="units"
                hint="How many homes the building contains."
              >
                <Input id="units" name="units" type="number" min={1} max={2000} defaultValue={12} placeholder="12" />
              </Field>
            ) : null}
            {/*
              The phase, where the property uses them.

              Required rather than optional, and pre-set to whichever chip the reader is looking
              through: where a property has phases, everything in it belongs to one, and a blank
              option here is how the bucket the operator refused gets created anyway.
            */}
            {phases.length ? (
              <Field label="Phase" htmlFor="phaseId" required>
                <Select id="phaseId" name="phaseId" required defaultValue={currentPhase ?? phases[0].id}>
                  {phases.map((phase) => (
                    <option key={phase.id} value={phase.id}>
                      {phase.name}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {types.length ? (
              <Field label="Price list" htmlFor="typeId">
                <Select id="typeId" name="typeId" defaultValue="">
                  <option value="">Not on the price list</option>
                  {types.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                      {type.priceLabel ? ` — ${type.priceLabel}` : ""}
                    </option>
                  ))}
                </Select>
              </Field>
            ) : null}
            {shape === "units" ? (
              <Field label="Floor" htmlFor="floor" hint="A label on each — 4, Ground.">
                <Input id="floor" name="floor" placeholder="4" />
              </Field>
            ) : null}
            <div className="sm:col-span-6">
              <Button type="submit" disabled={run.isPending}>
                {run.isPending
                  ? "Adding…"
                  : shape === "units"
                    ? `Add the ${noun.many}`
                    : shape === "whole"
                      ? "Add the building"
                      : "Add the building and its units"}
              </Button>
            </div>
          </form>
        ) : null}

        {lens.length === 0 ? (
          <EmptyState
            title={currentPhase ? "Nothing is in this phase yet." : `No ${noun.many} yet.`}
            hint={`A property with no ${noun.many} has nothing to sell. A numbered run goes in one go — one action, not one form each.`}
            action={
              canEdit ? (
                <Button onClick={() => setAdding(true)}>Add {noun.many}</Button>
              ) : null
            }
          />
        ) : (
          <TreeTable
            head={
              <tr>
                <th>{titleCase(noun.one)}</th>
                <th>Description</th>
                <th className="num">Size</th>
                <th className="num">Asking price</th>
                <th>Status</th>
                <th>Buyer</th>
                {showCommission ? <th>Commission</th> : null}
                {/* Two columns rather than one: whether it is advertised, and what it is
                    advertised with. A switch on a row with no photograph publishes a blank card. */}
                <th>Listing</th>
                {canEdit ? <th /> : null}
              </tr>
            }
          >
            {[...roots, ...orphans].map((root) => {
              const children = childrenOf(root.id).filter((child) => shownIds.has(child.id));
              /*
                A block is a subheading, and only a block.

                `levelOf` on the server decides what a block is — not for sale, and at the top — so
                this and the API cannot disagree about which rows become headings. Everything else,
                including a building somebody has chosen to sell whole, is an ordinary row: it is a
                thing for sale and belongs in the columns.
              */
              const isBlock = root.level === "block";
              return (
                <Fragment key={root.id}>
                  <NodeRow
                    node={root}
                    estateId={id}
                    phases={phases}
                    types={types}
                    sells={estate.sells}
                    noun={noun}
                    leadPhotoId={leadPhotos.get(root.id) ?? null}
                    canEdit={canEdit}
                    showCommission={showCommission}
                    openPanel={openPanel}
                    setOpenPanel={setOpenPanel}
                    onSplit={() => setSplitting(root.id)}
                    onWhole={() => setMerging(root.id)}
                    canReserve={can("lease.edit")}
                    onReserve={() => setReserving(root.id)}
                    heading={isBlock}
                    childCount={children.length}
                  />
                  {children.map((child) => (
                    <NodeRow
                      key={child.id}
                      node={child}
                      estateId={id}
                      phases={phases}
                      types={types}
                      sells={estate.sells}
                      noun={noun}
                      leadPhotoId={leadPhotos.get(child.id) ?? null}
                      canEdit={canEdit}
                      showCommission={showCommission}
                      openPanel={openPanel}
                      setOpenPanel={setOpenPanel}
                      onSplit={() => setSplitting(child.id)}
                      onWhole={() => setMerging(child.id)}
                      canReserve={can("lease.edit")}
                      onReserve={() => setReserving(child.id)}
                    />
                  ))}
                </Fragment>
              );
            })}
          </TreeTable>
        )}
      </Card>

      {/*
        The pictures, under the list rather than over it.

        They used to lead the screen, on the reasoning that a buyer's first question is what a
        property looks like. That is true of the *public* page and false of this one: a developer
        opens their own scheme to find out what is left, and a gallery between them and the answer
        is the module being heavy again. Item 2 — the head answers what is for sale, then the list.
      */}
      <Card padded={false} className="mb-6">
        <div className="border-b border-ink-200 p-4">
          <CardTitle
            hint={`Photographs of the property itself — the entrance, the roads, the show ${noun.one}. The first is the leading image and is the one a buyer sees first.`}
          >
            Photographs of {estate.name}
          </CardTitle>
        </div>
        <div className="p-4">
          <PhotoGallery owner="estate" ownerId={id} canEdit={canEdit} noun="property" />
        </div>
      </Card>

      {/*
        And the drawing, under the pictures.

        Under rather than over, because the photographs are what make somebody want the property and
        the plan is what they read once they do — and because a scheme with no plan yet should not
        open on an empty box where its pictures ought to be.
      */}
      <LayoutPlanCard estateId={id} plan={overview.data.layout ?? null} canEdit={canEdit} />

      <PriceListCard
        estateId={id}
        types={types}
        phases={phases}
        nodes={nodes}
        sales={sales.data?.purchases ?? []}
        sells={estate.sells}
        noun={noun}
        canEdit={canEdit}
      />

      {/*
        The rate at the top of the chain.

        Set here and every phase, price list line and item below uses it; any of them may override
        it. Empty is "nobody has said", which is not the same as 0 — "nothing is paid on this
        property" is a decision, and the two are stored differently so that setting the property's
        rate later does not silently overwrite one.

        **It is earned on the property amount, never on fees.** The operator: *"The marketer
        commission is on the property amount, not fees."* The base is the agreed price frozen on the
        sale, and a fee an agency happens to collect alongside it is not in it.
      */}
      {canEdit ? (
        <Card className="mb-6">
          <CardTitle hint="Inherited by every phase, price list line and item below, and overridable on any of them.">
            Commission rate
          </CardTitle>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              const values = formValues(event.currentTarget);
              setRate.mutate({
                commissionBp: commissionBpFrom(values.commissionBp),
                commissionBasis: values.commissionBasis,
              });
            }}
          >
            <Field label="Rate for this property" htmlFor="estate-commission" hint="Per cent. Leave it empty for none.">
              <Input
                id="estate-commission"
                name="commissionBp"
                type="number"
                min={0}
                max={100}
                step="0.01"
                defaultValue={commissionPercentValue(estate.commissionBp)}
                placeholder="2.5"
                className="w-32"
              />
            </Field>
            {/*
              When it is earned. The rate says how much; this says on what day.

              **All four agree on the total and differ only on the day**, so changing this moves
              when a marketer is paid and never how much — which is the sentence that makes it safe
              to change. `collections` is the default and is what most developers can afford: a
              share of every instalment as it is collected, rather than four years of commission on
              the day the papers are signed.
            */}
            <Field
              label="Earned when"
              htmlFor="estate-commission-basis"
              hint="Changing this moves when a marketer is paid, never how much. Sales already recorded keep the answer they were sold on."
            >
              <Select
                id="estate-commission-basis"
                name="commissionBasis"
                className="w-56"
                defaultValue={estate.commissionBasis ?? "collections"}
              >
                {Object.values(COMMISSION_BASIS_INFO).map((info) => (
                  <option key={info.value} value={info.value}>
                    {info.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Button type="submit" disabled={setRate.isPending}>
              {setRate.isPending ? "Saving…" : "Save the rate"}
            </Button>
            {/*
              What changing it does, and — the half that matters — what it does not.

              A developer will not touch a rate they think might reach a sale already agreed, and a
              screen that does not say so leaves them to find out by trying it on somebody's live
              deal. The rate is frozen on the purchase at the moment of sale; the sale's own screen
              says the same thing from the other side, in the same words.
            */}
            <p className="text-xs text-ink-500">
              {estate.commissionBp === null
                ? "No rate is set on this property, so nothing below inherits one."
                : `${commissionLabel(estate.commissionBp)} unless a phase, a price list line or a single ${noun.one} says otherwise.`}{" "}
              It is earned on the agreed price and never on fees. Changing either of these moves what
              the next sale earns and cannot reach a sale already recorded — those keep the rate and
              the basis they were agreed on.
            </p>
          </form>
        </Card>
      ) : null}

      {/*
        How this property reserves.

        Beside the commission rate rather than in a settings screen of its own, because both are
        answers about how this scheme is sold and both are read by the same person on the same
        afternoon. Only for somebody who may edit the property: these are the property's terms, not
        one buyer's.
      */}
      {canEdit ? <ReservationDefaultsCard estateId={id} estate={estate} noun={noun} /> : null}

      {reserving !== undefined ? (
        <ReserveModal
          estateId={id}
          /* An empty string is "the property, nothing chosen yet", which the dialog draws as a
             picker. A row's id fixes what is being held and takes the picker away. */
          plotId={reserving || null}
          onClose={() => setReserving(undefined)}
        />
      ) : null}

      {splittingNode ? (
        <SplitModal
          node={splittingNode}
          estateId={id}
          affected={saleOn(splittingNode)}
          types={types}
          noun={noun}
          onClose={() => setSplitting(null)}
        />
      ) : null}

      {mergingNode ? (
        <WholeModal
          node={mergingNode}
          estateId={id}
          children={childrenOf(mergingNode.id)}
          affected={childrenOf(mergingNode.id).filter((child) => child.sellable).flatMap(saleOn)}
          noun={noun}
          onClose={() => setMerging(null)}
        />
      ) : null}
    </div>
  );
}
